import axios from "axios";

// Resolve the backend base URL. Order of precedence:
//  1. Runtime override: window.__APP_CONFIG__.backendUrl (from public/config.js, set per-deployment
//     via the BACKEND_URL env var at container start — no rebuild needed).
//  2. REACT_APP_BACKEND_URL (build-time; used in this platform and most build pipelines).
//  3. Hostname-derived fallback for Docker/AWS builds where neither is set:
//     - single-origin (localhost / bare IP / self-hosted): same origin as the page.
//     - multi-tenant SaaS ("tenant.deinsaas.com"): "https://api-tenant.deinsaas.com".
function getApiUrl() {
  if (typeof window === "undefined") return "";

  const host = window.location.hostname;     // z.B. demo.entraconsole.com ODER demo.portal.entraconsole.com
  const protocol = window.location.protocol;   // https:

  // 1. Fallback für dein lokales Entwickeln (npm start)
  if (host === "localhost" || host === "127.0.0.1") {
    return "http://localhost:8000";
  }

  // 2. AWS / SaaS-Modus: Wenn ".portal." in der URL vorkommt, auf ".api." umbiegen
  if (host.includes(".portal.entraconsole.com")) {
    return `${protocol}//${host.replace(".portal.", ".api.")}`;
  }

  // 3. Dein aktueller Server-Modus / Single-Origin:
  // Wenn kein ".portal." im Host steht (z.B. demo.entraconsole.com),
  // bleibt die API-Basis-URL exakt dieselbe wie die des Frontends.
  return window.location.origin;
}

const BASE_URL = getApiUrl().replace(/\/+$/, "");
export const API = `${BASE_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- SaaS-only global infrastructure-error handling --------------------------------------------
// In our multi-tenant SaaS deployment a shared static frontend sits behind Azure Front Door while
// each tenant backend is a separate Container App. When a tenant backend is UNPROVISIONED the edge
// returns 502 (or a routing 404); when it is SUSPENDED (scaled to 0 replicas) it returns 503.
// We turn those infrastructure responses into friendly full-page redirects. This is DORMANT unless
// the frontend is flagged as a SaaS build, so a transient 502 during a self-hosted backend restart
// (or a normal application 404) never hijacks navigation.
const TENANT_NOT_FOUND_PATH = "/tenant-not-found";
const EXPIRED_PATH = "/expired";

function isSaasFrontend() {
  const rt = typeof window !== "undefined" && window.__APP_CONFIG__ && window.__APP_CONFIG__.saasMode;
  if (rt === true || rt === "true") return true;
  const env = process.env.REACT_APP_SAAS_MODE;
  return env === "true" || env === "1";
}

function hardRedirect(path) {
  if (typeof window === "undefined") return;
  if (window.location.pathname === path) return; // already there — avoid a redirect loop
  window.location.replace(path);
}

// A "no response" failure = the browser never got an HTTP status back: DNS failure, connection
// refused, TLS error, CORS rejection or timeout. This is EXACTLY what happens when a tenant backend
// does not exist (unprovisioned) — the edge cannot proxy, so there is no 502/503/404 to key off. We
// must treat these too, not only responses that carry a status. (Request cancellations are ignored.)
function isNoResponseError(error) {
  if (!error || error.response) return false;
  const code = error.code || "";
  const msg = (error.message || "").toLowerCase();
  if (code === "ERR_CANCELED" || msg === "canceled" || msg === "cancelled") return false;
  return true; // ERR_NETWORK, ECONNABORTED (timeout), DNS/TLS/CORS, …
}

// Confirmatory probe (bypasses this axios instance to avoid recursion). The provisioning health
// endpoint is unsigned, public and exempt from the SaaS origin gate, so a live tenant answers it.
//   200            -> "healthy"  (the original failure was transient — do NOT redirect)
//   503            -> "expired"  (backend suspended / scaled to zero)
//   any other / no response -> "gone" (backend truly unreachable -> tenant not found)
async function probeTenantHealth() {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 6000);
  try {
    const r = await fetch(`${API}/provision/health`, {
      method: "GET",
      signal: ctrl.signal,
      cache: "no-store",
    });
    if (r.status === 503) return "expired";
    if (r.status >= 200 && r.status < 400) return "healthy";
    return "gone"; // 404 / 502 / other -> no live backend behind the edge
  } catch (e) {
    return "gone"; // network/DNS/TLS/CORS failure -> backend does not exist
  } finally {
    clearTimeout(timer);
  }
}

let _infraRedirecting = false; // ensure we only probe/redirect once per page life

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isSaasFrontend() && !_infraRedirecting) {
      // (1) The edge answered with an infrastructure status.
      if (error && error.response) {
        const { status, data } = error.response;
        // Application errors carry a JSON `detail` (FastAPI). Infrastructure errors from the edge
        // (backend missing/suspended) do not — that is how we tell a routing 404 from an app 404.
        const isAppError = data && typeof data === "object" && "detail" in data;
        if (status === 503) {
          _infraRedirecting = true;
          hardRedirect(EXPIRED_PATH);
          return new Promise(() => {}); // never resolves — halts all further execution in this chain
        }
        if (status === 502 || (status === 404 && !isAppError)) {
          _infraRedirecting = true;
          hardRedirect(TENANT_NOT_FOUND_PATH);
          return new Promise(() => {}); // never resolves — halts all further execution in this chain
        }
      } else if (isNoResponseError(error)) {
        // (2) The browser got NO response at all — most likely an unprovisioned tenant backend.
        // Confirm with a cheap health probe before hijacking navigation, so a transient blip during
        // a cold start does not falsely eject a healthy tenant.
        const verdict = await probeTenantHealth();
        if (verdict === "expired") {
          _infraRedirecting = true;
          hardRedirect(EXPIRED_PATH);
          return new Promise(() => {});
        }
        if (verdict === "gone") {
          _infraRedirecting = true;
          hardRedirect(TENANT_NOT_FOUND_PATH);
          return new Promise(() => {});
        }
        // "healthy" -> fall through and let the caller handle the transient error locally.
      }
    }
    // Everything else (400, 401, 500, application 404s, …) passes through for local handling.
    return Promise.reject(error);
  }
);

// Public one-shot health check (no redirect side-effects) for the status screens to poll.
//   "healthy" | "expired" | "gone"
export async function checkTenantHealth() {
  return probeTenantHealth();
}

// Whether this build behaves as a multi-tenant SaaS frontend (drives the infra-error UX).
export function isSaasMode() {
  return isSaasFrontend();
}

// One-time boot check: called by the auth bootstrap BEFORE the first data call. In SaaS mode it
// probes the tenant backend's health directly, so an unprovisioned (or suspended) tenant lands on the
// status screen immediately — no need to wait for /auth/me to fail. No-op on self-hosted.
//   "expired" -> redirect /expired · "gone" -> redirect /tenant-not-found · "healthy"/"skip" -> continue
export async function bootTenantHealthCheck() {
  if (!isSaasFrontend()) return "skip";
  if (typeof window !== "undefined" &&
      (window.location.pathname === TENANT_NOT_FOUND_PATH || window.location.pathname === EXPIRED_PATH)) {
    return "skip"; // already on a status screen
  }
  const verdict = await probeTenantHealth();
  if (verdict === "expired") {
    _infraRedirecting = true;
    hardRedirect(EXPIRED_PATH);
    return verdict;
  }
  if (verdict === "gone") {
    _infraRedirecting = true;
    hardRedirect(TENANT_NOT_FOUND_PATH);
    return verdict;
  }
  return "healthy";
}

export function apiErr(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}
